/** oidc-client-ts stores the session under `oidc.user:<authority>:<client_id>`. */
const OIDC_KEY_PREFIX = 'oidc.user:'

const TOKEN_FIELDS = ['access_token', 'id_token', 'refresh_token'] as const

/**
 * Reads the OIDC session tokens out of browser storage.
 *
 * The redactor can only remove values it is given, and the app JWT alone is not
 * the whole set: `main.tsx` persists the OIDC session to localStorage so a
 * 30-day refresh token survives a closed tab. That refresh token is the most
 * durable credential the browser holds and it is **opaque, not a JWT**, so no
 * server-side pattern would ever match it — stripping it here is the only place
 * it can be caught.
 *
 * The storage key embeds the authority and client id, both deployment-specific,
 * so the entry is discovered by prefix rather than named.
 *
 * Every failure yields an empty list rather than throwing: private browsing and
 * blocked site data both make localStorage throw on access, and a bundle must
 * still be sendable. The cost of returning nothing is a redaction that misses,
 * which is why this is paired with — not a substitute for — sending over TLS to
 * a tenant-scoped receiver.
 */
export function collectOidcSecrets(): string[] {
  const found: string[] = []

  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i)
      if (key === null || !key.startsWith(OIDC_KEY_PREFIX)) continue

      const raw = window.localStorage.getItem(key)
      if (raw === null) continue

      try {
        const session = JSON.parse(raw) as Record<string, unknown>
        for (const field of TOKEN_FIELDS) {
          const value = session[field]
          if (typeof value === 'string' && value !== '') found.push(value)
        }
      } catch {
        // A malformed entry costs us that one session's tokens, not the bundle.
      }
    }
  } catch {
    return []
  }

  return found
}
